import React from 'react';
import { Paper } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import {
  RecurringRevenueCoverage,
  RecurringRevenueRow,
} from '@interfaces/reports/recurringRevenue';
import {
  SnapshotForCoverage,
  formatCoverageShare,
  isRowCoverageDeclarable,
  resolveMrrCoverage,
  resolveRowCoverage,
} from '@utils/resolveMrrCoverage';

/**
 * The number formatters moved to reportFormat, which the other report screens share.
 *
 * Re-exported from here rather than left duplicated: this module was their first home and both this
 * screen's components and its tests import them from it, so moving the definitions without the
 * re-export would be a rename dressed up as a refactor.
 */
import {
  formatBucket,
  formatMoney,
  formatRate,
  formatSignedMoney,
} from '../reportFormat';

export { formatBucket, formatMoney, formatRate, formatSignedMoney };

/**
 * Whether the COUNT is the half that is short - and never when the response did not send it.
 *
 * `total` is null when the coverage never arrived, and `covered < null` coerces to a comparison against zero,
 * which reads as "nothing is missing" on precisely the payload that told us nothing. Every copy branch that
 * quotes the counts goes through here. See #267.
 */
const countIsShort = (covered: number, total: number | null) =>
  typeof total === 'number' && covered < total;

/**
 * What the money on this screen speaks for, said on the screen rather than in a tooltip.
 *
 * Silence is the failure mode being fixed. A producer reconciling the MRR against their sales finds a gap and
 * concludes the report is broken - or worse, does not notice, because an understated MRR looks exactly like a
 * shrinking one. Rendered inline, above the figures, so it is read before them and not after the conclusion.
 *
 * Nothing at all when coverage is complete: a permanent banner is a banner nobody reads, and then the one time
 * it matters it is invisible.
 *
 * WHICH MAGNITUDE THE COPY MAY STATE depends on the instant, and it is the difference this note exists to keep
 * straight. For the snapshot the API now reports the uncovered MONEY, so the note names it - as an upper bound,
 * because that figure is deliberately over-estimated. For the series the share still counts SUBSCRIPTIONS, and
 * there the copy must refuse a magnitude: 90 of 100 subscriptions says nothing about how much revenue is missing,
 * since the omitted ones can hold pricier or cheaper plans. An earlier version said the MRR was lower "nessa
 * proporção" for both; codex caught it on #264.
 */
export const MrrCoverageNote = ({
  coverage,
  instant,
  snapshot,
}: {
  coverage: RecurringRevenueCoverage;
  instant: 'current' | 'opening';
  /** The snapshot this coverage describes. See resolveMrrCoverage: passed whole, never a figure at a time. */
  snapshot: SnapshotForCoverage;
}) => {
  const verdict = resolveMrrCoverage(coverage, instant, snapshot);
  if (verdict.kind === 'full') return null;

  const border =
    verdict.kind === 'unavailable'
      ? 'border-l-[#dc2626]'
      : 'border-l-[#f59e0b]';

  return (
    <Paper className={`p-main mb-main border-l-4 ${border}`}>
      {verdict.kind === 'unpriced' ? (
        <>
          <Typography className="text-sm">
            <strong>O MRR histórico está estimado.</strong> O período
            selecionado alcança datas anteriores ao início do preço por período,
            então a série usa o preço mais recente de cada assinatura aplicado
            para trás.
          </Typography>
          <Typography className="text-sm text-lightBlack pt-sm">
            Um aumento de preço aparece como se sempre tivesse existido, o que
            infla o passado. As entradas e saídas de assinantes continuam
            exatas; o nível de MRR, não. Selecione um período mais recente para
            ver o valor medido.
          </Typography>
        </>
      ) : verdict.kind === 'unpriceable' ? (
        <>
          <Typography className="text-sm">
            <strong>Não foi possível medir a cobertura do valor.</strong> O
            relatório não informou quanto ficou de fora, então o MRR abaixo pode
            estar subestimado — e não dá para dizer em quanto.
          </Typography>
          <Typography className="text-sm text-lightBlack pt-sm">
            {/*
              The counts are the one thing that can be trusted in this state - only the money is unknown - so
              they are shown and named as such. Skipped entirely when there is no base to count: this state is
              also reached when the coverage itself is missing from the payload, and "0 de 0 assinaturas" would
              read as an empty community rather than as a report that arrived incomplete.
            */}
            {typeof verdict.total === 'number' && verdict.total > 0 && (
              <>
                A contagem de assinantes continua exata: {verdict.covered} de{' '}
                {verdict.total} assinaturas têm o período pago conhecido.{' '}
              </>
            )}
            Se isso persistir, é sinal de que o relatório está desatualizado.
          </Typography>
        </>
      ) : verdict.kind === 'unusable' ? (
        <>
          <Typography className="text-sm">
            <strong>Valor indisponível.</strong> O relatório devolveu um MRR
            inválido, então não há número para exibir nem cobertura para medir.
          </Typography>
          <Typography className="text-sm text-lightBlack pt-sm">
            {/*
              Withheld rather than printed, unlike the unpriceable state one branch up. There the coverage is
              unknown and the figure is fine; here the figure itself is the broken part, and rendering a
              negative MRR under a warning about the report presents it as an observation. Codex, #267.
            */}
            Nada é somado a partir de um valor assim. A contagem de assinantes
            ao lado continua exata; se isso persistir, é um defeito no
            relatório, não na sua base.
          </Typography>
        </>
      ) : verdict.kind === 'unavailable' ? (
        <>
          <Typography className="text-sm">
            <strong>Valor indisponível.</strong> Só{' '}
            {formatCoverageShare(verdict.share)}
            {verdict.basis === 'value'
              ? ' do MRR pôde ser confirmado'
              : ' da base tem o período pago conhecido'}
            {/*
              The count is quoted only when the COUNT is what is short. The value share can fall below the
              threshold with every subscription counted, and "(100 de 100 assinaturas)" beside "só 40% pôde ser
              confirmado" reads as a contradiction - the reassuring half being the one a producer believes.
            */}
            {countIsShort(verdict.covered, verdict.total)
              ? ` (${verdict.covered} de ${verdict.total} assinaturas)`
              : ''}
            , pouco demais para somar um MRR.
          </Typography>
        </>
      ) : (
        <>
          <Typography className="text-sm">
            <strong>
              {/*
                "pelo menos", and only on the value share, because that share is a LOWER bound by construction:
                `uncoveredValue` is an upper bound, so it inflates the denominator and understates coverage. The
                paragraph below already calls the amount a ceiling, and a percentage stated as exact beside it
                contradicted it. The count share carries no such hedge - a count is exact. Codex, #267.
              */}
              O valor cobre {verdict.basis === 'value' ? 'pelo menos ' : ''}
              {formatCoverageShare(verdict.share)}
              {/*
                Named after the share that PRODUCED the verdict, not after the field that happens to be present.
                The minimum of the two can come from the count while the uncovered value is known - 90 of 100
                subscriptions with R$ 50 missing is 90% of the BASE and about 99% of the MRR - and calling that
                90% "do MRR" states a number the data does not support. Codex, #267.
              */}
              {verdict.basis === 'value' ? ' do MRR' : ' da base'}
            </strong>{' '}
            {/*
              The new model permits value omission WITHOUT count omission - a subscription can be counted as
              covered and still have its money held out, which is the case this whole change exists to expose.
              Asserting "100 de 100 assinaturas ... as que faltam" there states a gap in the count that does not
              exist, on the sentence whose job is to be exact about what is missing. Codex, #267.
            */}
            {/*
              "PODE estar", never "está", and the reason is the same ceiling the share is hedged with. The
              omission is an upper bound that counts anything ambiguous as missing, so the amount actually
              absent can be zero and the coverage can really be 100% - the sentence asserting the MRR IS
              understated contradicted the paragraph below it, which calls the figure a ceiling. It holds for a
              count-based verdict too: an unattributed subscription can be a courtesy one, worth nothing, and
              then nothing is missing either. Codex, #267.
            */}
            {countIsShort(verdict.covered, verdict.total) ? (
              <>
                — {verdict.covered} de {verdict.total} assinaturas. O MRR abaixo
                pode estar subestimado: as que faltam não entram na soma.
              </>
            ) : (
              <>
                — o MRR abaixo pode estar subestimado. Toda assinatura ativa
                está contada; parte do valor delas é que não pôde ser
                confirmada.
              </>
            )}
          </Typography>
          <Typography className="text-sm text-lightBlack pt-sm">
            {verdict.uncoveredValue === null ? (
              <>
                Quanto falta em dinheiro <strong>não</strong> dá para deduzir
                dessa fatia — ela conta assinaturas, e as que faltam podem ter
                planos mais caros ou mais baratos que a média.{' '}
              </>
            ) : (
              <>
                {/*
                  An UPPER bound, and the copy has to say so. The API's test for "covered" is stricter than the
                  MRR's own scope wherever it cannot be exact, so this figure counts anything ambiguous as
                  missing - stating it as an exact amount would be the same overclaim in the other direction.
                */}
                Falta até <strong>{formatMoney(verdict.uncoveredValue)}</strong>{' '}
                por mês — um teto, não um valor exato: o cálculo conta como
                ausente tudo o que não conseguiu confirmar.{' '}
              </>
            )}
            {countIsShort(verdict.covered, verdict.total)
              ? 'São assinaturas cujo período pago não é conhecido, em geral tocadas por reembolso, chargeback, MED ou edição manual. A contagem de assinantes continua completa; a diferença está só no dinheiro.'
              : 'É valor que o cálculo não conseguiu confirmar, em geral de assinaturas tocadas por reembolso, chargeback, MED ou edição manual. A contagem de assinantes continua completa; a diferença está só no dinheiro.'}
          </Typography>
        </>
      )}
    </Paper>
  );
};

/**
 * The MRR of a card whose coverage is too low to state, without printing a smaller number.
 *
 * Never R$ 0,00 in that state: zero is a real answer - a community with no active subscribers - so using it for
 * "could not be computed" collides with the one case a producer must be able to trust.
 */
/**
 * "MRR em risco", which the whole-base coverage CANNOT vouch for.
 *
 * `coverage.current` describes the entire active base. mrrAtRisk describes a subset - the subscriptions whose
 * renewal is already off - and an aggregate share says nothing about a subset's own coverage. Codex's scenario on
 * #264: 98 of 100 covered reads as complete and silences every note, while the two uncovered ones are the only
 * cancellation-scheduled subscriptions, so the card renders R$ 0,00 beside "2 já desligaram a renovação".
 *
 * The API exposes no subset coverage, and inventing one would be worse than the bug. What IS detectable from the
 * data in hand is the CONTRADICTION: people scheduled to cancel and no money at risk cannot both be true. That is
 * the exact shape of the failure, so it is what gets caught - zero money with nobody leaving stays a real zero.
 *
 * Returns the caption too, because a bare dash with no reason beside it is the defect this PR exists to remove.
 */
export const describeAtRiskMrr = (
  snapshot: SnapshotForCoverage & {
    mrrAtRisk: number;
    cancellationScheduled: number;
  },
  coverage: RecurringRevenueCoverage,
  format: (value: number) => string,
) => {
  const scheduled = snapshot.cancellationScheduled;
  const people = `${scheduled} já desligaram a renovação`;

  /**
   * A figure that came back broken is withheld and NAMED, before any coverage question is asked about it.
   *
   * A negative or non-numeric `mrrAtRisk` was formatted and printed - "-R$ 5,00 · 3 já desligaram a renovação"
   * reads as an observation about the business rather than as a defect in the report. The same rule the MRR
   * itself got one function along, applied to the figure derived beside it. Codex, #267.
   */
  if (!Number.isFinite(snapshot.mrrAtRisk) || snapshot.mrrAtRisk < 0)
    return {
      value: '—',
      caption: `${people} · valor inválido no relatório`,
      isWithheld: true,
    };

  /**
   * Nobody leaving is a CERTAIN zero, and coverage has nothing to say about it.
   *
   * The counts in this report are exact for any instant - only the money is as good as the period coverage - so a
   * scheduled-cancellation count of zero proves the subset is empty. Falling through to the coverage logic turned
   * that certainty into "— · valor não disponível" below 50%, and into "R$ 0,00 · pode ser maior" above it: both
   * claiming doubt about a figure the count settles. The mirror of the contradiction below, and codex found it on
   * #264 one round after that one.
   */
  if (scheduled === 0)
    return { value: format(0), caption: people, isWithheld: false };

  if (snapshot.mrrAtRisk === 0)
    return {
      value: '—',
      caption: `${people} · valor não disponível`,
      isWithheld: true,
    };

  /**
   * The VALUE being formatted is the at-risk money; the COVERAGE is judged on the whole base's MRR.
   *
   * Two different figures on purpose, and the pair is easy to get wrong in the direction that hides things: using
   * mrrAtRisk as the denominator of the coverage share would divide by a base this subset is not, and would report
   * a coverage no figure on the screen has.
   */
  const value = formatCoveredMoney(
    snapshot.mrrAtRisk,
    coverage,
    'current',
    format,
    snapshot,
  );

  if (value === '—')
    return {
      value,
      caption: `${people} · valor não disponível`,
      isWithheld: true,
    };

  /**
   * Qualified on ANY omission, not on the 97% threshold, and the reason is the size of the subset.
   *
   * The threshold is calibrated for the whole base, where a 3% gap is a rounding difference. This figure describes
   * a handful of subscriptions - the ones with renewal already off - so the same two uncovered rows can be its
   * entire population. Codex's case on #264: 98 of 100 covered reads as full and silences everything, while one
   * covered cancellation worth R$ 10 sits beside two uncovered expensive ones and the card prints R$ 10.
   *
   * The value is kept rather than withheld, because unlike the MRR it is a secondary figure and a producer acting
   * on "at least this much is leaving" is not misled by a floor - only by a floor presented as a total. So the
   * caption says which it is. Withholding still applies to the two cases above: the contradiction, and an
   * aggregate too low to state at all.
   */
  /**
   * NOT gated on pricedFromPeriods, and that gate was a repeat of a mistake already fixed one function along.
   *
   * That flag describes the SERIES - whether each bucket was priced at what the subscription was paying then - and
   * the snapshot has no such question: it is today's base at today's price. resolveMrrCoverage stopped
   * short-circuiting `current` on it for exactly this reason, and then this conjunct reintroduced it here.
   *
   * The effect was the worst available combination: a window reaching below the pricing floor made `omitted` false,
   * the 97% threshold suppressed the general note as well, and the card presented a floor as a complete value.
   * Codex, #264.
   */
  /**
   * The MONEY decides whether this figure can be understated, and the counts only stand in when it is unknown.
   *
   * "pode ser maior" is a claim about the amount, so a reported `uncoveredValue` settles it in both directions: a
   * positive one qualifies the card even with every subscription counted - the state the value figure exists to
   * expose - and an explicit ZERO is an upper bound proving nothing was omitted, so the qualifier must go, however
   * many subscriptions went unattributed. Reading the counts alongside it put "pode ser maior" on a card the data
   * proves complete, which is the same contradiction the verdict itself carried. Codex, #267.
   *
   * With no money reported the counts are all there is, and an unmeasurable coverage is not evidence of a
   * complete one - so any count gap still qualifies.
   */
  const uncovered = coverage.current.uncoveredValue;
  /**
   * USABLE, not merely finite. A negative amount is malformed - the resolver already refuses to measure with it -
   * and `isFinite` alone accepted it, so `> 0` came out false and a card with subscriptions missing lost its
   * qualifier on the strength of a number nothing else in the module trusts. Unusable money falls back to the
   * counts, like every other unusable value. Codex, #267.
   */
  const pricedOmission =
    Number.isFinite(uncovered) && (uncovered as number) >= 0;
  /**
   * With no usable amount AND no counts either, nothing vouches for this figure - so it is qualified.
   *
   * The counts decide only when they exist: `total` is null when the coverage never arrived, and treating that
   * as "nothing is missing" would drop the qualifier on the emptiest possible evidence.
   */
  const omitted = pricedOmission
    ? (uncovered as number) > 0
    : typeof coverage.current.total !== 'number' ||
      countIsShort(coverage.current.covered, coverage.current.total);

  return {
    value,
    caption: omitted ? `${people} · pode ser maior` : people,
    isWithheld: false,
  };
};

/**
 * A money figure, withheld when the coverage behind it is too low to state.
 *
 * `value` is what gets printed and the SNAPSHOT is what the coverage is judged on - the same number for the MRR
 * card and deliberately different for the derived ones (ARR, at-risk, average ticket), which are all vouched for
 * by the same base. `unavailable` and `unusable` withhold - too little coverage to state, and a figure that came
 * back broken. `unpriceable` prints the figure with the note above it saying the coverage could not be measured,
 * because not knowing the share is not evidence that it is low.
 */
export const formatCoveredMoney = (
  value: number,
  coverage: RecurringRevenueCoverage,
  instant: 'current' | 'opening',
  format: (value: number) => string,
  snapshot: SnapshotForCoverage,
) =>
  ['unavailable', 'unusable'].includes(
    resolveMrrCoverage(coverage, instant, snapshot).kind,
  )
    ? '—'
    : format(value);

const HeaderCell = ({ children }: { children: React.ReactNode }) => (
  <th className="text-left px-md py-sm text-xs font-semibold text-lightBlack whitespace-nowrap">
    {children}
  </th>
);

const Cell = ({
  children,
  muted,
}: {
  children: React.ReactNode;
  muted?: boolean;
}) => (
  <td
    className={`px-md py-sm text-sm whitespace-nowrap ${
      muted ? 'text-lightBlack' : 'text-[#374151]'
    }`}
  >
    {children}
  </td>
);

/**
 * The series, as a table.
 *
 * A table rather than a chart on purpose: the interesting reading is not the shape of the MRR line but the
 * three numbers behind each of its moves - who arrived, who left, and what the net was. A line hides
 * exactly that, and a producer who sees MRR flat wants to know whether it was quiet or whether ten arrivals
 * cancelled out ten departures.
 *
 * The horizontal scroll container is deliberate: on a phone this is nine columns, and letting the page
 * scroll sideways instead would move the whole layout.
 */
export const RecurringRevenueTable = ({
  rows,
  isMonthly,
}: {
  rows: RecurringRevenueRow[];
  isMonthly: boolean;
}) => {
  if (!rows.length)
    return (
      <Typography className="text-sm text-lightBlack">
        Nenhum período para exibir.
      </Typography>
    );

  /**
   * Per row, because the ends of the range cannot see this.
   *
   * A subscription with no known period that both arrives and churns inside the window is absent from the
   * opening instant and from the current one, so both can read complete while a bucket in the middle is missing
   * value. The range-level note says nothing about it; only the row can.
   */
  const markedRows = rows.filter(isRowCoverageDeclarable);

  return (
    <>
      <div className="overflow-x-auto shadow-default rounded-default">
        <table className="w-full border-collapse">
          <thead className="bg-[#F9FAFB]">
            <tr>
              <HeaderCell>Período</HeaderCell>
              <HeaderCell>Assinantes no fim</HeaderCell>
              <HeaderCell>Entraram</HeaderCell>
              <HeaderCell>Saíram</HeaderCell>
              <HeaderCell>Churn</HeaderCell>
              <HeaderCell>MRR</HeaderCell>
              <HeaderCell>MRR novo</HeaderCell>
              <HeaderCell>MRR perdido</HeaderCell>
              <HeaderCell>MRR líquido</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.period} className="border-t border-[#E5E7EB]">
                <Cell>{formatBucket(row.period, isMonthly)}</Cell>
                <Cell>{row.activeSubscribers}</Cell>
                <Cell muted>{row.newSubscribers}</Cell>
                <Cell muted>{row.churnedSubscribers}</Cell>
                <Cell>{formatRate(row.churnRate)}</Cell>
                <Cell>
                  {/*
                    Withheld on the row too, by the same threshold as the range. A bucket priced for a tenth of
                    its subscribers prints a figure indistinguishable from a month the community collapsed.
                  */}
                  {resolveRowCoverage(row).kind === 'unavailable'
                    ? '—'
                    : formatMoney(row.mrr)}
                  {isRowCoverageDeclarable(row) && (
                    <sup
                      className="text-[#b45309] pl-[2px]"
                      title={`Cobre ${row.coveredSubscribers} de ${row.activeSubscribers} assinantes deste período`}
                    >
                      *
                    </sup>
                  )}
                </Cell>
                <Cell muted>{formatMoney(row.newMrr)}</Cell>
                <Cell muted>{formatMoney(row.churnedMrr)}</Cell>
                <Cell>
                  <span
                    className={
                      row.netMrr < 0
                        ? 'text-red-600'
                        : row.netMrr > 0
                        ? 'text-green-600'
                        : undefined
                    }
                  >
                    {formatSignedMoney(row.netMrr)}
                  </span>
                </Cell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/*
      Spelled out under the table, not left to the hover.

      The marker itself is reachable by mouse only, so on a phone it would be an asterisk with no explanation.
      The footnote names how many rows carry it, which is also what tells a producer whether this is one odd
      month or the whole series.
    */}
      {markedRows.length > 0 && (
        <Typography className="pt-sm text-xs text-lightBlack">
          * {markedRows.length}{' '}
          {markedRows.length === 1 ? 'período tem' : 'períodos têm'} assinantes
          cujo período pago não é conhecido, então o MRR daquela linha é menor
          que o real. A contagem de assinantes continua completa.
        </Typography>
      )}
    </>
  );
};
